import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { User, Check, ChevronDown } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { Button } from '@deepguide-ai/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@deepguide-ai/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@deepguide-ai/ui/popover';
import { ScrollArea } from '@deepguide-ai/ui/scroll-area';
import { MemberAvatar } from '@/components/compliance/MemberAvatar';
import { cn } from '@/lib/utils';

interface AssigneeFilterProps {
  value: string; // 'all' | 'assigned' | 'unassigned' | 'agents' | userId
  onChange: (value: string) => void;
  className?: string;
  portalled?: boolean;
}

/**
 * Reusable assignee filter component with search
 * Used for filtering tests/tasks by assignee
 */
export function AssigneeFilter({ value, onChange, className, portalled }: AssigneeFilterProps) {
  const [open, setOpen] = useState(false);

  // Fetch workspace members
  const membersQuery = useQuery(
    trpc.workspaceMembers.list.queryOptions()
  );
  const members = membersQuery.data || [];

  // Get display label for current selection
  const getDisplayLabel = () => {
    // Short enough to sit un-truncated in a dense toolbar; the icon beside
    // it already says this is the assignee axis.
    if (value === 'all') return 'Assignees';
    if (value === 'assigned') return 'Assigned';
    if (value === 'unassigned') return 'Unassigned';
    if (value === 'agents') return 'Vera';
    const member = members.find((m) => m.userId === value);
    return member?.user?.name || member?.user?.email || 'Unknown';
  };

  // Get selected member for avatar display
  const selectedMember = !['all', 'assigned', 'unassigned', 'agents'].includes(value)
    ? members.find((m) => m.userId === value)
    : null;

  const handleSelect = (newValue: string) => {
    onChange(newValue);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn('h-9 w-[150px] justify-between rounded-sm bg-card text-[12px] font-normal', className)}
        >
          <div className="flex items-center gap-2 min-w-0">
            {selectedMember ? (
              <MemberAvatar
                person={{
                  image: selectedMember.user?.image,
                  name: selectedMember.user?.name,
                  email: selectedMember.user?.email,
                }}
                size="xs"
              />
            ) : (
              <User className="h-4 w-4 text-muted-foreground shrink-0" />
            )}
            <span className="truncate">{getDisplayLabel()}</span>
          </div>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[240px] p-0" align="start" portalled={portalled}>
        <Command>
          <CommandInput placeholder="Search members..." />
          <CommandList className="max-h-none overflow-hidden">
            <ScrollArea
              type="always"
              className="h-[280px]"
              viewClassName="[&>div]:!block"
            >
              <CommandEmpty>No members found.</CommandEmpty>
              <CommandGroup>
                <CommandItem
                  value="all"
                  onSelect={() => handleSelect('all')}
                >
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span>All Assignees</span>
                  {value === 'all' && (
                    <Check className="ml-auto h-4 w-4" />
                  )}
                </CommandItem>
                <CommandItem
                  value="assigned"
                  onSelect={() => handleSelect('assigned')}
                >
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span>Assigned</span>
                  {value === 'assigned' && (
                    <Check className="ml-auto h-4 w-4" />
                  )}
                </CommandItem>
                <CommandItem
                  value="unassigned"
                  onSelect={() => handleSelect('unassigned')}
                >
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span>Unassigned</span>
                  {value === 'unassigned' && (
                    <Check className="ml-auto h-4 w-4" />
                  )}
                </CommandItem>
                <CommandItem
                  value="vera"
                  keywords={['ai', 'agent']}
                  onSelect={() => handleSelect('agents')}
                >
                  <MemberAvatar person={{ isAgent: true }} size="xs" />
                  <span>Vera</span>
                  {value === 'agents' && (
                    <Check className="ml-auto h-4 w-4" />
                  )}
                </CommandItem>
              </CommandGroup>
              {members.length > 0 && (
                <>
                  <CommandSeparator />
                  <CommandGroup heading="Team Members">
                    {members.map((member) => (
                      <CommandItem
                        key={member.userId}
                        value={member.user?.name || member.user?.email || member.userId}
                        onSelect={() => handleSelect(member.userId)}
                      >
                        <MemberAvatar
                          person={{
                            image: member.user?.image,
                            name: member.user?.name,
                            email: member.user?.email,
                          }}
                          size="xs"
                        />
                        <span className="truncate">
                          {member.user?.name || member.user?.email || 'Unknown'}
                        </span>
                        {value === member.userId && (
                          <Check className="ml-auto h-4 w-4" />
                        )}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </>
              )}
            </ScrollArea>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
